// English draughts (8x8, forced captures, men promote on the back rank,
// kings move one step any diagonal). Shared by the server + the client so
// legal-move highlighting can't disagree with the authority.

export const CK_N = 8;
export type CkBoard = string; // 64 chars, row-major: '.' r R b B

const inb = (r: number, c: number) => r >= 0 && r < 8 && c >= 0 && c < 8;
const idx = (r: number, c: number) => r * 8 + c;

export function initBoard(): CkBoard {
  let b = "";
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const dark = (r + c) % 2 === 1;
      b += dark && r < 3 ? "b" : dark && r > 4 ? "r" : ".";
    }
  }
  return b;
}

/** 0 = red side (r/R), 1 = black side (b/B), -1 = empty */
export function owner(ch: string): number {
  return ch === "r" || ch === "R" ? 0 : ch === "b" || ch === "B" ? 1 : -1;
}
const king = (ch: string) => ch === "R" || ch === "B";
function dirs(ch: string): [number, number][] {
  if (king(ch)) return [[-1, -1], [-1, 1], [1, -1], [1, 1]];
  return ch === "r" ? [[-1, -1], [-1, 1]] : [[1, -1], [1, 1]];
}

export interface CkMove { from: number; to: number; caps: number[] }

function chainJumps(board: string, pos: number, from: number, caps: number[], out: CkMove[]) {
  const ch = board[pos]!;
  const r = Math.floor(pos / 8), c = pos % 8;
  let extended = false;
  for (const [dr, dc] of dirs(ch)) {
    const mr = r + dr, mc = c + dc, lr = r + 2 * dr, lc = c + 2 * dc;
    if (!inb(lr, lc)) continue;
    const mid = idx(mr, mc), land = idx(lr, lc);
    if (board[land] !== "." || caps.includes(mid)) continue;
    const mo = owner(board[mid]!);
    if (mo < 0 || mo === owner(ch)) continue;

    const nb = board.split("");
    nb[land] = nb[pos]!;
    nb[pos] = ".";
    let promoted = false;
    if (nb[land] === "r" && lr === 0) { nb[land] = "R"; promoted = true; }
    if (nb[land] === "b" && lr === 7) { nb[land] = "B"; promoted = true; }
    const ncaps = [...caps, mid];
    extended = true;
    if (promoted) {
      out.push({ from, to: land, caps: ncaps });
    } else {
      const before = out.length;
      chainJumps(nb.join(""), land, from, ncaps, out);
      if (out.length === before) out.push({ from, to: land, caps: ncaps });
    }
  }
  if (!extended && caps.length && !out.some((m) => m.from === from && m.to === pos)) {
    out.push({ from, to: pos, caps });
  }
}

/** all full-chain legal moves for side (0 red-ish / 1 black-ish). Forced capture applies. */
export function legalMoves(b: CkBoard, side: number): CkMove[] {
  const jumps: CkMove[] = [];
  for (let p = 0; p < 64; p++) {
    if (owner(b[p]!) !== side) continue;
    chainJumps(b, p, p, [], jumps);
  }
  if (jumps.length) return jumps;

  const simple: CkMove[] = [];
  for (let p = 0; p < 64; p++) {
    if (owner(b[p]!) !== side) continue;
    const ch = b[p]!, r = Math.floor(p / 8), c = p % 8;
    for (const [dr, dc] of dirs(ch)) {
      const nr = r + dr, nc = c + dc;
      if (inb(nr, nc) && b[idx(nr, nc)] === ".") simple.push({ from: p, to: idx(nr, nc), caps: [] });
    }
  }
  return simple;
}

export function applyMove(b: CkBoard, mv: CkMove): CkBoard {
  const nb = b.split("");
  nb[mv.to] = nb[mv.from]!;
  nb[mv.from] = ".";
  for (const cap of mv.caps) nb[cap] = ".";
  const tr = Math.floor(mv.to / 8);
  if (nb[mv.to] === "r" && tr === 0) nb[mv.to] = "R";
  if (nb[mv.to] === "b" && tr === 7) nb[mv.to] = "B";
  return nb.join("");
}

export function pieceCount(b: CkBoard, side: number): number {
  let n = 0;
  for (const ch of b) if (owner(ch) === side) n++;
  return n;
}
